import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Button, Input } from '@besiktning/ui'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { Logo } from '../components/Logo'
import { apiUrl } from '../lib/apiBase'
import { t } from '../i18n'

// Claiming an imported garage from the link in an outreach email.
//
// The token in the URL only says WHICH garage — it authenticates nobody. The
// code we mail to the garage's address on file is what proves ownership, so a
// forwarded link cannot take a garage over.
//
// 'expired' is deliberately not a dead end: someone clicking a month-old link is
// a prospect actively trying to convert, and losing them there is the most
// expensive failure in this funnel. They get a fresh link in one click, sent to
// the address we already hold — never to one typed into this page.
type Step = 'loading' | 'otp' | 'expired' | 'resent' | 'invalid' | 'claimed' | 'phone'

interface GarageSummary {
  id: number
  name: string
  kommun: string | null
  address: string | null
}

export default function ClaimGarage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { login } = useAuthStore()

  const [step, setStep] = useState<Step>('loading')
  const [garage, setGarage] = useState<GarageSummary | null>(null)
  const [maskedEmail, setMaskedEmail] = useState('')
  const [openRequests, setOpenRequests] = useState(0)
  const [code, setCode] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('')
  const [phoneSent, setPhoneSent] = useState(false)
  const [authToken, setAuthToken] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const started = useRef(false)

  // StrictMode double-invokes effects and this one sends an email, so guard it.
  useEffect(() => {
    if (!token || started.current) return
    started.current = true

    const start = async () => {
      try {
        const res = await fetch(apiUrl('/garage_claims/start'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        })
        const data = await res.json()

        if (res.ok) {
          setGarage(data.garage)
          setMaskedEmail(data.email || '')
          setOpenRequests(data.open_requests || 0)
          setStep('otp')
          return
        }

        switch (data.code) {
          case 'claim_expired':
            setStep('expired')
            break
          case 'already_claimed':
            setStep('claimed')
            break
          default:
            // account_unavailable / signup_pending carry a message worth showing.
            setError(data.error || '')
            setStep('invalid')
        }
      } catch {
        setError(t('claim.errGeneric'))
        setStep('invalid')
      }
    }
    start()
  }, [token])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const res = await fetch(apiUrl('/garage_claims/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code: code.trim() }),
      })
      const data = await res.json()

      if (!res.ok) {
        setError(data.error || t('claim.errWrongCode'))
        return
      }

      login(data.user, data.token, data.memberships || [], data.current_membership, false)
      setAuthToken(data.token)

      // Phone is asked for AFTER the claim, never as a wall in front of it.
      if (data.needs_phone) setStep('phone')
      else navigate('/', { replace: true })
    } catch {
      setError(t('claim.errGeneric'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setIsLoading(true)
    setError('')
    try {
      const res = await fetch(apiUrl('/garage_claims/resend'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      if (res.ok) {
        setMaskedEmail(data.email || maskedEmail)
        setStep('resent')
      } else {
        setError(data.error || t('claim.errGeneric'))
      }
    } catch {
      setError(t('claim.errGeneric'))
    } finally {
      setIsLoading(false)
    }
  }

  const postAuthed = (path: string, body: Record<string, string>) =>
    fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      body: JSON.stringify(body),
    })

  const handlePhoneSend = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      const res = await postAuthed('/auth/phone/request_otp', { phone: phone.trim() })
      if (res.ok) setPhoneSent(true)
      else setError((await res.json()).error || t('claim.phoneErr'))
    } catch {
      setError(t('claim.phoneErr'))
    } finally {
      setIsLoading(false)
    }
  }

  const handlePhoneVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')
    try {
      const res = await postAuthed('/auth/phone/verify', { phone: phone.trim(), code: phoneCode.trim() })
      if (res.ok) navigate('/', { replace: true })
      else setError((await res.json()).error || t('claim.phoneErr'))
    } catch {
      setError(t('claim.phoneErr'))
    } finally {
      setIsLoading(false)
    }
  }

  const openRequestsLine = () => {
    const bucket = openRequests === 0 ? 'zero' : openRequests === 1 ? 'one' : 'other'
    return t(`claim.openRequests.${bucket}`, { count: openRequests })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Logo className="h-20 sm:h-24" />
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          {step === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">{t('claim.loading')}</p>
            </div>
          )}

          {step === 'otp' && (
            <form onSubmit={handleVerify}>
              <h2 className="text-lg font-semibold mb-1">{t('claim.heading')}</h2>
              <p className="text-sm text-gray-600 mb-4">
                {t('claim.intro', { email: maskedEmail, garage: garage?.name ?? '' })}
              </p>

              {openRequests > 0 && (
                <p className="text-sm font-medium text-gray-900 bg-blue-50 rounded-lg px-3 py-2 mb-4">
                  {openRequestsLine()}
                </p>
              )}

              <Input
                label={t('claim.codeLabel')}
                type="text"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                required
                className="mb-4"
              />

              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

              <Button type="submit" className="w-full" isLoading={isLoading} disabled={!code.trim()}>
                {t('claim.submit')}
              </Button>
            </form>
          )}

          {step === 'expired' && (
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-1">{t('claim.expiredHeading')}</h2>
              <p className="text-sm text-gray-600 mb-4">{t('claim.expiredBody')}</p>
              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
              <Button className="w-full" isLoading={isLoading} onClick={handleResend}>
                {t('claim.resend')}
              </Button>
            </div>
          )}

          {step === 'resent' && (
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-1">{t('claim.resentHeading')}</h2>
              <p className="text-sm text-gray-600">{t('claim.resentBody', { email: maskedEmail })}</p>
            </div>
          )}

          {step === 'claimed' && (
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-1">{t('claim.claimedHeading')}</h2>
              <p className="text-sm text-gray-600 mb-6">{t('claim.claimedBody')}</p>
              <Link
                to="/login"
                className="inline-block rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                {t('claim.goToLogin')}
              </Link>
            </div>
          )}

          {step === 'invalid' && (
            <div className="text-center">
              <h2 className="text-lg font-semibold mb-1">{t('claim.invalidHeading')}</h2>
              <p className="text-sm text-gray-600 mb-6">{error || t('claim.invalidBody')}</p>
              <Link
                to="/login"
                className="inline-block rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                {t('claim.goToLogin')}
              </Link>
            </div>
          )}

          {step === 'phone' && (
            <form onSubmit={phoneSent ? handlePhoneVerify : handlePhoneSend}>
              <h2 className="text-lg font-semibold mb-1">{t('claim.phoneHeading')}</h2>
              <p className="text-sm text-gray-600 mb-4">{t('claim.phoneBody')}</p>

              {phoneSent ? (
                <Input
                  label={t('claim.phoneCodeLabel')}
                  type="text"
                  inputMode="numeric"
                  placeholder="123456"
                  value={phoneCode}
                  onChange={(e) => setPhoneCode(e.target.value)}
                  maxLength={6}
                  required
                  className="mb-4"
                />
              ) : (
                <Input
                  label={t('claim.phoneLabel')}
                  type="tel"
                  placeholder="+46 70 123 45 67"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  className="mb-4"
                />
              )}

              {error && <p className="text-sm text-red-600 mb-4">{error}</p>}

              <Button type="submit" className="w-full" isLoading={isLoading}>
                {phoneSent ? t('claim.phoneVerify') : t('claim.phoneSend')}
              </Button>

              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className="w-full mt-3 text-sm text-blue-600 hover:text-blue-700"
              >
                {t('claim.phoneSkip')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
